import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { bookingsApi, portsApi } from '../services/api';
import api from '../services/api';
import PortSearch from '../components/PortSearch';
import { ArrowRight, ArrowLeft, Loader2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { containerTypes, cargoTypes, formatCurrency } from '../lib/utils';
import { ShipmentModeToggle, BulkCargoFields, BulkSummary, useBulkOptions, estimateBulkPrice, bulkPayload } from '../components/booking/BulkFields';
import { formatHs } from '../components/booking/HsCodeField';
import { CargoLinesEditor, newLine, linesPayload, linesTotals, validateLines } from '../components/booking/CargoLinesEditor';
import { Button, Field, Card, Badge, Tag } from '../components/sv';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const STEPS = ['Route and sailing', 'Cargo', 'Review and confirm'];
const SERVICE_TONE = { Express: 'success', Standard: 'info', Economy: 'neutral' };
const TODAY = new Date().toISOString().slice(0, 10);

function d(s, f) { try { return format(parseISO(s), f || 'dd MMM yyyy'); } catch (e) { return '—'; } }

export default function BookingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isAgent: staff } = useAuth();
  const bulkOptions = useBulkOptions();
  const preSelectedSchedule = location.state?.schedule;
  const quoteData = location.state?.quote;

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [ports, setPorts] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [sailingFrom, setSailingFrom] = useState(preSelectedSchedule?.departure_date ? preSelectedSchedule.departure_date.slice(0, 10) : '');
  const [selectedSchedule, setSelectedSchedule] = useState(preSelectedSchedule || null);
  const [formData, setFormData] = useState({
    origin_port: preSelectedSchedule?.origin_port || searchParams.get('origin') || quoteData?.origin_port || '',
    destination_port: preSelectedSchedule?.destination_port || searchParams.get('destination') || quoteData?.destination_port || '',
    shipment_mode: quoteData?.shipment_mode || (searchParams.get('mode') === 'bulk' ? 'bulk' : 'container'),
    bulk_cargo_type: quoteData?.bulk_cargo_type || 'dry_bulk', bulk_commodity: quoteData?.bulk_commodity || '',
    quantity_mt: quoteData?.quantity_mt || '', vessel_class: quoteData?.vessel_class || '', loading_terms: quoteData?.loading_terms || 'FIOST',
    cargo_type: (quoteData?.cargo_type && quoteData.cargo_type !== 'BULK') ? quoteData.cargo_type : 'FCL',
    container_type: (quoteData?.container_type && quoteData.container_type !== 'bulk') ? quoteData.container_type : '40ft',
    container_count: quoteData?.container_count || 1,
    special_requirements: '',
    cargo_lines: [newLine({ gross_weight_kg: quoteData?.weight || '' })],
    schedule_id: preSelectedSchedule?.id || '', vessel_name: preSelectedSchedule?.vessel_name || '', voyage_number: preSelectedSchedule?.voyage_number || '',
    departure_date: preSelectedSchedule?.departure_date || '', arrival_date: preSelectedSchedule?.arrival_date || '',
  });
  const [estimatedPrice, setEstimatedPrice] = useState(quoteData?.total_price || 0);

  useEffect(function () { portsApi.getAll().then(function (r) { setPorts(r.data); }).catch(function () {}); }, []);

  useEffect(function () {
    if (formData.origin_port && formData.destination_port) loadSchedules(); else setSchedules([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.origin_port, formData.destination_port, sailingFrom]);

  useEffect(function () {
    if (formData.shipment_mode === 'bulk') { setEstimatedPrice(estimateBulkPrice(bulkOptions, formData.bulk_commodity, Number(formData.quantity_mt))); return; }
    const basePrices = { '20ft': 1500, '40ft': 2500, '40ft_hc': 2800, '20ft_reefer': 2500, '40ft_reefer': 4000 };
    const base = basePrices[formData.container_type] || 2500;
    const price = base * formData.container_count * (formData.cargo_type === 'LCL' ? 0.6 : 1);
    setEstimatedPrice(Math.round(price * 1.15 + 150 * formData.container_count));
  }, [formData.container_type, formData.container_count, formData.cargo_type, formData.shipment_mode, formData.bulk_commodity, formData.quantity_mt, bulkOptions]);

  async function loadSchedules() {
    setLoadingSchedules(true);
    try {
      const params = { origin: formData.origin_port, destination: formData.destination_port };
      if (sailingFrom) params.departure_from = sailingFrom;
      const res = await api.get('/schedules/search?' + new URLSearchParams(params).toString());
      const list = res.data.schedules || [];
      setSchedules(list);
      if (!selectedSchedule && list.length > 0) selectSchedule(list[0]);
    } catch (e) { setSchedules([]); } finally { setLoadingSchedules(false); }
  }

  function changeSailingFrom(value) {
    setSailingFrom(value);
    setSelectedSchedule(null);
    setFormData(function (p) { return { ...p, schedule_id: '', vessel_name: '', voyage_number: '', departure_date: '', arrival_date: '' }; });
  }

  function handleChange(field, value) {
    setFormData(function (p) {
      const next = { ...p, [field]: value };
      if (field === 'origin_port' || field === 'destination_port') Object.assign(next, { schedule_id: '', vessel_name: '', voyage_number: '', departure_date: '', arrival_date: '' });
      return next;
    });
    if (field === 'origin_port' || field === 'destination_port') setSelectedSchedule(null);
  }

  function selectSchedule(s) {
    setSelectedSchedule(s);
    setFormData(function (p) { return { ...p, schedule_id: s.id, vessel_name: s.vessel_name, voyage_number: s.voyage_number, departure_date: s.departure_date, arrival_date: s.arrival_date }; });
  }

  const isBulk = formData.shipment_mode === 'bulk';
  const totals = linesTotals(formData.cargo_lines, isBulk);
  function validate(s) {
    if (s === 1) {
      if (!formData.origin_port || !formData.destination_port) return 'Select origin and destination ports';
      if (!selectedSchedule) return 'Select a sailing';
    }
    if (s === 2) {
      const err = validateLines(formData.cargo_lines, 'booking', isBulk);
      if (err) return err;
      if (isBulk && (!formData.bulk_commodity || Number(formData.quantity_mt) < 500)) return 'Select a bulk commodity and enter at least 500 metric tons';
    }
    return null;
  }
  function next() { const err = validate(step); if (err) { toast.error(err); return; } setStep(step + 1); }

  async function submit(e) {
    e.preventDefault();
    const err = validate(1) || validate(2); if (err) { toast.error(err); return; }
    setLoading(true);
    try {
      const payload = {
        ...bulkPayload(formData),
        origin_port: formData.origin_port, destination_port: formData.destination_port,
        cargo_type: formData.cargo_type, container_type: formData.container_type, container_count: formData.container_count,
        cargo_lines: linesPayload(formData.cargo_lines, isBulk), weight: isBulk ? undefined : totals.gross, pickup_date: formData.departure_date,
        special_requirements: formData.special_requirements,
        schedule_id: formData.schedule_id, vessel_name: formData.vessel_name, voyage_number: formData.voyage_number,
      };
      const res = await bookingsApi.create(payload);
      toast.success('Booking created');
      navigate('/dashboard/bookings', { state: { newBooking: res.data } });
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not create the booking'); } finally { setLoading(false); }
  }

  return (
    <div className="bg-gray-50 min-h-screen" data-testid="booking-page">
      <div className="bg-white border-b border-gray-200">
        <div className="sv-wrap py-10">
          <p className="sv-kicker">Booking</p>
          <h1 className="text-[36px] md:text-[40px] sv-display mt-2">{isBulk ? 'Book bulk cargo' : 'Book a container'}</h1>
          <ol className="flex flex-wrap gap-x-8 gap-y-2 mt-8" data-testid="booking-steps">
            {STEPS.map(function (label, i) {
              const n = i + 1; const state = n < step ? 'done' : n === step ? 'current' : 'todo';
              return (
                <li key={label} className={"flex items-center gap-3 text-[15px] " + (state === 'todo' ? 'text-gray-600' : 'text-ink')} data-testid={"step-indicator-" + n} data-state={state}>
                  <span className={"w-7 h-7 rounded-full grid place-items-center text-[13px] sv-mono " + (state === 'current' ? 'bg-ink text-white' : state === 'done' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600')}>{n}</span>
                  {label}
                </li>
              );
            })}
          </ol>
        </div>
      </div>

      <div className="sv-wrap py-8 pb-24 grid lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] gap-6 items-start">
        <form onSubmit={submit} className="grid gap-6">
          {step === 1 && (
            <div className="grid gap-6 animate-fadeInUp" data-testid="step-route">
              <Card title="Route" padding="p-8">
                <div className="grid md:grid-cols-2 gap-5">
                  <Field label="Origin port"><PortSearch ports={ports} value={formData.origin_port} onChange={function (v) { handleChange('origin_port', v); }} placeholder="Port, country or code" testId="origin-port" /></Field>
                  <Field label="Destination port"><PortSearch ports={ports} value={formData.destination_port} onChange={function (v) { handleChange('destination_port', v); }} placeholder="Port, country or code" testId="destination-port" /></Field>
                </div>
              </Card>
              <Card title="Sailing" padding="p-8" action={
                <label className="flex items-center gap-2 text-sm text-gray-600">
                  <span className="whitespace-nowrap">Sailings from</span>
                  <input type="date" value={sailingFrom} min={staff ? undefined : TODAY} onChange={function (e) { changeSailingFrom(e.target.value); }} className="sv-input sv-mono h-10 w-44" data-testid="sailing-from-date" />
                </label>
              }>
                {!formData.origin_port || !formData.destination_port ? (
                  <p className="text-gray-600 text-[15px]">Select both ports to see available sailings.</p>
                ) : loadingSchedules ? (
                  <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-gray-600" /></div>
                ) : schedules.length > 0 ? (
                  <div className="grid gap-2" data-testid="schedule-options">
                    {staff && sailingFrom && sailingFrom < TODAY && (
                      <p className="text-[13px] text-amber-900 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-1" data-testid="past-sailings-note">Showing sailings from {d(sailingFrom)}. Booking a sailing that has already departed records the shipment retrospectively — milestones can then be entered with their actual past dates.</p>
                    )}
                    {schedules.map(function (s) {
                      const sel = selectedSchedule?.id === s.id;
                      return (
                        <button key={s.id} type="button" onClick={function () { selectSchedule(s); }} data-testid={"schedule-option-" + s.id} data-departed={s.departed ? 'true' : 'false'} aria-pressed={sel}
                          className={"text-left grid md:grid-cols-[1.4fr_1fr_1fr_auto] gap-3 md:items-center p-4 rounded border transition-colors duration-[120ms] " + (sel ? 'border-ink bg-blue-100' : 'border-gray-300 hover:bg-gray-50')}>
                          <div><div className="text-[15px]">{s.vessel_name}</div><div className="sv-mono text-[13px] text-gray-600">{s.voyage_number}</div></div>
                          <div className="sv-mono text-sm">{d(s.departure_date)} <span className="text-gray-600">dep</span></div>
                          <div className="sv-mono text-sm">{d(s.arrival_date)} <span className="text-gray-600">arr · {s.transit_days} d</span></div>
                          <div className="flex items-center gap-2 md:justify-end">{s.departed && <Tag data-testid={"schedule-sailed-" + s.id}>Sailed</Tag>}<Badge tone={SERVICE_TONE[s.service_type] || 'neutral'}>{s.service_type}</Badge></div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-600 text-[15px]" data-testid="no-schedules">No sailings on this route {sailingFrom ? 'from ' + d(sailingFrom) : 'right now'}. Try another date, nearby ports or contact your agent.</p>
                )}
              </Card>
              <div className="flex justify-end"><Button type="button" onClick={next} disabled={!selectedSchedule} data-testid="step1-continue">Continue <ArrowRight className="h-[18px] w-[18px]" strokeWidth={1.75} /></Button></div>
            </div>
          )}

          {step === 2 && (
            <div className="grid gap-6 animate-fadeInUp" data-testid="step-cargo">
              <Card title="Cargo" padding="p-8">
                <div className="grid gap-6">
                  <ShipmentModeToggle value={formData.shipment_mode} onChange={function (v) { handleChange('shipment_mode', v); }} />
                  {isBulk ? <BulkCargoFields formData={formData} onChange={handleChange} options={bulkOptions} /> : (
                    <div className="grid md:grid-cols-3 gap-5">
                      <Field label="Cargo type"><select value={formData.cargo_type} onChange={function (e) { handleChange('cargo_type', e.target.value); }} className="sv-input" data-testid="cargo-type">{cargoTypes.map(function (t) { return <option key={t.value} value={t.value}>{t.label}</option>; })}</select></Field>
                      <Field label="Container type"><select value={formData.container_type} onChange={function (e) { handleChange('container_type', e.target.value); }} className="sv-input" data-testid="container-type">{containerTypes.map(function (t) { return <option key={t.value} value={t.value}>{t.label}</option>; })}</select></Field>
                      <Field label="Number of containers"><input type="number" min="1" max="100" value={formData.container_count} onChange={function (e) { handleChange('container_count', parseInt(e.target.value) || 1); }} className="sv-input sv-mono" data-testid="container-count" /></Field>
                    </div>
                  )}
                  <Field label="Products" hint={isBulk ? 'One line per commodity or grade. HS codes are suggested from the description — change them if your customs classification differs.' : 'One line per product — different cuts or grades usually carry different HS codes and are printed as separate lines on the bill of lading. Total weight is summed from the lines.'}>
                    <CargoLinesEditor lines={formData.cargo_lines} onChange={function (v) { handleChange('cargo_lines', v); }} mode="booking" isBulk={isBulk} bulkCommodity={isBulk ? formData.bulk_commodity : undefined} />
                  </Field>
                  <Field label="Special requirements" hint="Optional. Temperature, hazardous class, out-of-gauge dimensions."><textarea rows={3} value={formData.special_requirements} onChange={function (e) { handleChange('special_requirements', e.target.value); }} className="sv-input h-auto py-3" data-testid="special-requirements" /></Field>
                </div>
              </Card>
              <div className="flex justify-between"><Button type="button" variant="secondary" onClick={function () { setStep(1); }} data-testid="step2-back"><ArrowLeft className="h-[18px] w-[18px]" strokeWidth={1.75} /> Back</Button><Button type="button" onClick={next} data-testid="step2-continue">Continue <ArrowRight className="h-[18px] w-[18px]" strokeWidth={1.75} /></Button></div>
            </div>
          )}

          {step === 3 && (
            <div className="grid gap-6 animate-fadeInUp" data-testid="step-confirm">
              <Card title="Review" padding="p-8">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="p-4 bg-gray-50 rounded"><p className="text-sm text-gray-600">Origin</p><p className="mt-0.5">{formData.origin_port}</p></div>
                  <div className="p-4 bg-gray-50 rounded"><p className="text-sm text-gray-600">Destination</p><p className="mt-0.5">{formData.destination_port}</p></div>
                  {isBulk ? <BulkSummary formData={formData} /> : (
                    <>
                      <div className="p-4 bg-gray-50 rounded"><p className="text-sm text-gray-600">Cargo</p><p className="mt-0.5">{formData.container_count} × {formData.container_type} · {formData.cargo_type}</p></div>
                      <div className="p-4 bg-gray-50 rounded"><p className="text-sm text-gray-600">Total gross weight</p><p className="mt-0.5 sv-mono" data-testid="review-weight">{totals.gross.toLocaleString()} kg{totals.packages ? ' · ' + totals.packages.toLocaleString() + ' packages' : ''}</p></div>
                    </>
                  )}
                  <div className="p-4 bg-gray-50 rounded sm:col-span-2" data-testid="review-products">
                    <p className="text-sm text-gray-600">Products ({formData.cargo_lines.length})</p>
                    <ul className="mt-1 divide-y divide-gray-200">
                      {formData.cargo_lines.map(function (l, i) {
                        const digits = (l.hs_code || '').replace(/\D/g, '');
                        return (
                          <li key={l._key} className="py-2 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1" data-testid={'review-product-' + i}>
                            <span>{l.description}</span>
                            <span className="text-sm text-gray-700 sv-mono">{digits ? 'HS ' + formatHs(digits) : 'HS —'}{!isBulk && l.package_count ? ' · ' + Number(l.package_count).toLocaleString() + ' ' + (l.package_type || 'cartons').toLowerCase() : ''}{!isBulk && l.gross_weight_kg ? ' · ' + Number(l.gross_weight_kg).toLocaleString() + ' kg' : ''}{isBulk && l.quantity_mt ? ' · ' + Number(l.quantity_mt).toLocaleString() + ' MT' : ''}</span>
                          </li>
                        );
                      })}
                    </ul>
                    {formData.special_requirements && <p className="text-sm text-gray-700 mt-2">{formData.special_requirements}</p>}
                  </div>
                </div>
              </Card>
              <div className="flex justify-between"><Button type="button" variant="secondary" onClick={function () { setStep(2); }} data-testid="step3-back"><ArrowLeft className="h-[18px] w-[18px]" strokeWidth={1.75} /> Back</Button><Button type="submit" disabled={loading} data-testid="submit-booking">{loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Confirm booking'}</Button></div>
            </div>
          )}
        </form>

        <aside className="grid gap-6 lg:sticky lg:top-24">
          <Card title="Summary" padding="p-6" data-testid="booking-summary">
            {selectedSchedule ? (
              <div className="grid gap-3 text-[15px]">
                <div className="flex items-center justify-between gap-3"><span>{selectedSchedule.vessel_name}</span><span className="flex items-center gap-2">{selectedSchedule.departed && <Tag data-testid="summary-sailed-tag">Sailed</Tag>}<Tag mono>{selectedSchedule.voyage_number}</Tag></span></div>
                <div className="flex justify-between text-gray-700"><span>Departs</span><span className="sv-mono">{d(selectedSchedule.departure_date)}</span></div>
                <div className="flex justify-between text-gray-700"><span>Arrives</span><span className="sv-mono">{d(selectedSchedule.arrival_date)}</span></div>
                <div className="flex justify-between text-gray-700"><span>Transit</span><span className="sv-mono">{selectedSchedule.transit_days} days</span></div>
                <div className="flex justify-between text-gray-700"><span>Cut-off</span><span className="sv-mono">{d(selectedSchedule.cutoff_date, 'dd MMM, HH:mm')}</span></div>
              </div>
            ) : <p className="text-gray-600 text-[15px]">Select a route and sailing to see the summary.</p>}
            <div className="border-t border-gray-200 mt-5 pt-5">
              <div className="text-sm text-gray-600">Estimated total</div>
              <div className="text-[32px] font-light sv-mono mt-1" data-testid="estimated-price">{formatCurrency(estimatedPrice)}</div>
              <p className="text-[13px] text-gray-600 mt-2">Ocean freight, fuel surcharge and {isBulk ? 'port charges' : 'terminal handling'}. Final invoice follows the published tariff.</p>
            </div>
          </Card>
          <div className="text-[13px] text-gray-600 px-1">By confirming you accept the <a href="/legal/bill-of-lading" className="sv-link">bill of lading terms</a>. Space is confirmed by your regional agent.</div>
        </aside>
      </div>
    </div>
  );
}
